import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as SubmitIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSiteStore } from '@/store/site.store';
import { submitPageAudit, type PageAuditRequest } from '@/services/customer/auditApi';

// ─── Target query chips ───────────────────────────────────────────────────────

function QueryChips({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft('');
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          label="Add target query (optional)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="e.g. best espresso machine"
          helperText="Press Enter or + to add"
          disabled={disabled}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={add}
          disabled={!draft.trim() || disabled}
          sx={{ minWidth: 40, px: 1 }}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {value.map((q) => (
          <Chip
            key={q}
            label={q}
            size="small"
            onDelete={disabled ? undefined : () => onChange(value.filter((x) => x !== q))}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function CustomerPageAuditPage() {
  const { selectedSite } = useSiteStore();
  const [pageUrl, setPageUrl] = useState('');
  const [queries, setQueries] = useState<string[]>([]);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState<PageAuditRequest | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      submitPageAudit({
        siteId: selectedSite!.id,
        pageUrl: pageUrl.trim() || undefined,
        targetQueries: queries.length > 0 ? queries : undefined,
        llmEnabled,
      }),
    onSuccess: (result) => { setSubmitted(result); setFormError(''); },
    onError: (e: Error) => setFormError(e.message),
  });

  const handleSubmit = () => {
    if (!selectedSite) { setFormError('Select a site first.'); return; }
    setFormError('');
    mutation.mutate();
  };

  const handleReset = () => { setSubmitted(null); setPageUrl(''); setQueries([]); };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Submit Page Audit</Typography>
        <Typography variant="body2" color="text.secondary">
          Request an AI-powered SEO audit for a specific page or your entire site.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 520 }}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!selectedSite && (
                <Alert severity="info">Select a site from the top bar first.</Alert>
              )}

              {submitted && (
                <Alert
                  severity="success"
                  action={<Button color="inherit" size="small" onClick={handleReset}>New</Button>}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Audit queued — Request #{submitted.id}
                  </Typography>
                  <Typography variant="caption">Status: {submitted.status}</Typography>
                  <br />
                  <Typography variant="caption">
                    Track progress in{' '}
                    <Link to="/customer/audit/tracking" style={{ color: 'inherit', fontWeight: 600 }}>
                      Audit History
                    </Link>.
                  </Typography>
                </Alert>
              )}

              <TextField
                label="Page URL"
                size="small"
                fullWidth
                value={pageUrl}
                onChange={(e) => setPageUrl(e.target.value)}
                placeholder="https://example.com/blog/my-post"
                helperText="Leave blank to audit the full site"
                disabled={!selectedSite || !!submitted}
              />

              <QueryChips
                value={queries}
                onChange={setQueries}
                disabled={!selectedSite || !!submitted}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={llmEnabled}
                    onChange={(e) => setLlmEnabled(e.target.checked)}
                    color="primary"
                    disabled={!selectedSite || !!submitted}
                  />
                }
                label={
                  <Tooltip title="AI analysis generates keyword relevance, content gap, and improvement recommendations.">
                    <span>Enable AI Analysis</span>
                  </Tooltip>
                }
              />

              {formError && <Alert severity="error">{formError}</Alert>}

              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SubmitIcon />}
                onClick={handleSubmit}
                disabled={!selectedSite || !!submitted || mutation.isPending}
              >
                {mutation.isPending ? 'Submitting…' : 'Submit Audit'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
